// General Health check app settings

import { pickBy, keys } from 'lodash';

import { StatusItemIssueType } from '@/apps/health_check/types/enums';

export const DISPLAY_NAME_MAX_CHARS = 128;
export const SEARCH_VALUE_MAX_CHARS = 32;
export const OPERATIONAL_INSTRUCTION_MAX_CHARS = 1024;

export const HIGHLIGHT_NEW_STATUS_ITEM_DURATION = 60000; // 60 seconds

export const LOCAL_STORAGE_FILTERS_AND_SORTING = [
  'contracts',
  'flattenContracts',
  'types',
  'severity',
  'vehicleIds',
  'blockIds',
  'sortField',
  'sortDirection',
  'showMuted',
  'showHealthy',
  'depots',
];

type VisibleForType = 'all' | 'healthCheckConfig' | 'faultAlarm';

type ConfigItemType = 'healthCheckConfig' | 'faultAlarm';

type ColumnConfig = {
  cellType:
    | 'text'
    | 'textArea'
    | 'switch'
    | 'radio'
    | 'number'
    | 'detailsPanel'
    | 'actions'
    | 'reference';
  width: number;
  scope: 'shared' | 'generic' | 'custom' | 'reference';
  maxLength?: number;
  visibleFor?: VisibleForType;
  widthFor?: Partial<Record<ConfigItemType, number>>;
  /**
   * Width used while the cross-view reference column is shown (CBP-1218).
   * The grid is a fixed 24 units, so the reference column can only be paid for
   * out of the other columns - see getColumnWidth.
   */
  widthWithReference?: Partial<Record<ConfigItemType, number>>;
};

type HeaderColumnConfig = {
  cellType?: never;
  width: number;
  scope: 'headerOnly';
  visibleFor?: VisibleForType;
  widthFor?: Partial<Record<ConfigItemType, number>>;
  widthWithReference?: Partial<Record<ConfigItemType, number>>;
};

type AllColumnConfig = ColumnConfig | HeaderColumnConfig;

// Configuration for each column in the health check settings table
// Also determines column order
export const columnConfigs: Record<string, AllColumnConfig> = {
  displayName: {
    cellType: 'textArea',
    width: 4,
    scope: 'shared',
    maxLength: DISPLAY_NAME_MAX_CHARS,
  },
  searchValue: {
    cellType: 'text',
    width: 3,
    scope: 'shared',
    maxLength: SEARCH_VALUE_MAX_CHARS,
    visibleFor: 'faultAlarm',
  },
  // Enabled and Actions keep their full width even with the reference column:
  // the "ENABLED" heading wraps below 3 units and Actions holds the Update
  // button. The reference column is paid for out of Severity and Threshold,
  // which carry the most slack.
  active: { cellType: 'switch', width: 3, scope: 'shared' },
  severity: {
    cellType: 'radio',
    width: 6,
    scope: 'shared',
    widthWithReference: { healthCheckConfig: 5 },
  },
  startTimeThresholdMinutes: {
    cellType: 'number',
    widthFor: { healthCheckConfig: 8 },
    widthWithReference: { healthCheckConfig: 5 },
    width: 5,
    scope: 'generic',
  },
  // Read-only cross-view reference (CBP-1218). Never rendered for fault alarms,
  // which have no guaranteed counterpart row to align against. Sits between
  // Threshold and Actions so the Update button keeps the right edge of the row.
  referenceValues: {
    cellType: 'reference',
    width: 0,
    widthWithReference: { healthCheckConfig: 4 },
    scope: 'reference',
    visibleFor: 'healthCheckConfig',
  },
  actions: { cellType: 'actions', width: 3, scope: 'generic' },
  temperatureCheckpoints: {
    cellType: 'detailsPanel',
    widthFor: { healthCheckConfig: 11 },
    widthWithReference: { healthCheckConfig: 8 },
    width: 8,
    scope: 'custom',
  },
  // Header cell for the Threshold column. It used to span the Update button as
  // well, which is why it was wider than the input beneath it. Update now has
  // its own header cell, so this has to match startTimeThresholdMinutes exactly
  // or the heading drifts off the input it names.
  threshold: {
    widthFor: { healthCheckConfig: 8 },
    widthWithReference: { healthCheckConfig: 5 },
    width: 5,
    scope: 'headerOnly',
  },
};

export const isColumnVisibleForItem = (
  columnKey: string,
  type?: 'healthCheckConfig' | 'faultAlarm'
): boolean => {
  const config = columnConfigs[columnKey];
  if (!config) {
    return false;
  }

  const visibleFor = config.visibleFor ?? 'all';
  return visibleFor === 'all' || visibleFor === type;
};

export const getColumnWidth = (
  columnKey: string,
  type?: ConfigItemType,
  showReference = false
): number => {
  const config = columnConfigs[columnKey];
  if (!config) {
    return 0;
  }

  if (showReference && type && config.widthWithReference?.[type]) {
    return config.widthWithReference[type];
  }

  if (type && config.widthFor?.[type]) {
    return config.widthFor[type];
  }

  return config.width;
};

// Derived values from columnConfigs
export const sharedCols = keys(
  pickBy(columnConfigs, (config) => config.scope === 'shared')
);

export const genericConfigCols = keys(
  pickBy(columnConfigs, (config) => config.scope === 'generic')
);

export const detailsPanelConfigCols = keys(
  pickBy(columnConfigs, (config) => config.scope === 'custom')
);

export const REFERENCE_COLUMN_KEY = 'referenceValues';

/** Column holding the Update button. It carries its own, deliberately blank,
 * header cell: the button already reads "Update". */
export const ACTIONS_COLUMN_KEY = 'actions';

export const headerConfigCols = [
  ...sharedCols,
  'threshold',
  ACTIONS_COLUMN_KEY,
] as const;

/**
 * Header columns, with the reference column inserted before Actions when it is
 * shown - the Update button stays on the right edge of the row.
 * The data rows build their own column list, so both sides must be derived from
 * the same widths or the header stops lining up with the values beneath it.
 */
export const getHeaderConfigCols = (showReference = false): string[] =>
  showReference
    ? [...sharedCols, 'threshold', REFERENCE_COLUMN_KEY, ACTIONS_COLUMN_KEY]
    : [...headerConfigCols];

export const renderableCols = keys(
  pickBy(columnConfigs, (config) => config.scope !== 'headerOnly')
);

// Map issue types to their specific column scope
const issueTypeToScope: Partial<
  Record<StatusItemIssueType, 'generic' | 'custom'>
> = {
  [StatusItemIssueType.LowTemperature]: 'custom', // --> renders temperatureCheckpoints instead of threshold + actions columns
};

export const getItemSpecificCols = (type: StatusItemIssueType | undefined) => {
  const scope = (type && issueTypeToScope[type]) ?? 'generic';

  return keys(pickBy(columnConfigs, (config) => config.scope === scope));
};

// Map column keys to their detail panel component names
export const detailsPanelComponentMap: Record<string, string> = {
  temperatureCheckpoints: 'TemperatureCheckpoints',
};

export const colWidth = Object.fromEntries(
  Object.entries(columnConfigs).map(([key, config]) => [key, config.width])
) as Record<string, number>;

export const sharedCells = Object.fromEntries(
  Object.entries(columnConfigs)
    .filter(([, config]) => config.scope === 'shared')
    .map(([key, config]) => [key, config.cellType])
) as Record<string, string>;

export const thresholdCells = Object.fromEntries(
  Object.entries(columnConfigs)
    .filter(
      ([, config]) => config.scope === 'generic' || config.scope === 'custom'
    )
    .map(([key, config]) => [key, config.cellType])
) as Record<string, string>;
